import React from 'react';
import { Link } from 'react-router-dom';
import { PageTitle } from '../components/PageTitle';
import { ArrowLeftIcon, BarChart2Icon, TrendingUpIcon, ClockIcon } from 'lucide-react';
import { Helmet } from 'react-helmet';
export function CaseStudy1() {
  return <div data-id="element-368">
      <PageTitle title="Product Turnaround Strategy" canonical="/case-study-1" description="How Katalina Bock revived a declining $110M+ product platform, delivering a 21% performance turnaround in just 6 months through strategic planning and execution." keywords="product turnaround, growth strategy, performance improvement, organic growth, marketing strategy, product management, Katalina Bock" data-id="element-369" />
      <Helmet data-id="element-370">
        <script type="application/ld+json" data-id="element-371">
          {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: 'Product Turnaround Strategy',
          description: 'How Katalina Bock revived a declining $110M+ product platform, delivering a 21% performance turnaround in just 6 months.',
          author: {
            '@type': 'Person',
            name: 'Katalina Bock'
          },
          publisher: {
            '@type': 'Organization',
            name: 'Katalina Bock',
            logo: {
              '@type': 'ImageObject',
              url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/katalina-profile-image.jpg'
            }
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': 'https://katalinabock.com/case-study-1'
          }
        })}
        </script>
        <script type="application/ld+json" data-id="element-372">
          {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [{
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: 'https://katalinabock.com/'
          }, {
            '@type': 'ListItem',
            position: 2,
            name: 'Portfolio',
            item: 'https://katalinabock.com/portfolio'
          }, {
            '@type': 'ListItem',
            position: 3,
            name: 'Product Turnaround Strategy',
            item: 'https://katalinabock.com/case-study-1'
          }]
        })}
        </script>
      </Helmet>
      <section className="bg-gradient-to-r from-[#0096B4] to-[#7DCCD8] text-white py-16" data-id="element-373">
        <div className="container mx-auto px-4" data-id="element-374">
          <Link to="/" className="inline-flex items-center text-white hover:text-[#E0F5F8] mb-6 transition-colors duration-200" data-id="element-375">
            <ArrowLeftIcon size={16} className="mr-1" data-id="element-376" />
            Back to Home
          </Link>
          <h1 className="text-4xl font-bold mb-4" data-id="element-377">
            Product Turnaround Strategy
          </h1>
          <p className="text-xl mb-6 text-white max-w-3xl" data-id="element-378">
            How I revived a declining $110M+ product platform, delivering a 21%
            performance turnaround in just 6 months through a self-funded
            recovery plan.
          </p>
          <div className="flex flex-wrap gap-6 mt-8" data-id="element-379">
            <div className="flex items-center" data-id="element-380">
              <BarChart2Icon size={20} className="mr-2 text-[#E0F5F8]" data-id="element-381" />
              <span className="text-white" data-id="element-382">$110M Product Platform</span>
            </div>
            <div className="flex items-center" data-id="element-383">
              <TrendingUpIcon size={20} className="mr-2 text-[#E0F5F8]" data-id="element-384" />
              <span className="text-white" data-id="element-385">+21% Growth Swing</span>
            </div>
            <div className="flex items-center" data-id="element-386">
              <ClockIcon size={20} className="mr-2 text-[#E0F5F8]" data-id="element-387" />
              <span className="text-white" data-id="element-388">6-Month Timeframe</span>
            </div>
          </div>
        </div>
      </section>
      <section className="py-16 bg-white" data-id="element-389">
        <div className="container mx-auto px-4" data-id="element-390">
          <div className="max-w-3xl mx-auto" data-id="element-391">
            <div className="mb-12" data-id="element-392">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-393">
                The Challenge
              </h2>
              <p className="text-gray-700 mb-4" data-id="element-394">
                I needed to reverse consistent 12% year-over-year declines in a
                $108M "long tail" product portfolio with no additional budget
                allocation.
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2" data-id="element-395">
                <li data-id="element-396">
                  These product lines represented 40% of the business but
                  weren't receiving adequate focus or investment
                </li>
                <li data-id="element-397">
                  The company typically only supported the top 2-3 product
                  lines, neglecting these smaller platforms
                </li>
                <li data-id="element-398">
                  Despite excellent propositions, these products needed a way to
                  deliver "new news to old SKUs" within existing constraints
                </li>
              </ul>
            </div>
            <div className="mb-12" data-id="element-399">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-400">
                My Approach
              </h2>
              <ol className="list-decimal pl-6 space-y-6" data-id="element-401">
                <li data-id="element-402">
                  <div data-id="element-403">
                    <span className="font-medium text-gray-900" data-id="element-404">
                      Leveraging Existing Channels
                    </span>
                    <ul className="list-disc pl-6 mt-2 text-gray-700 space-y-1" data-id="element-405">
                      <li data-id="element-406">
                        Integrated products into ongoing organic channels and
                        site-wide promotions without additional media spend
                      </li>
                      <li data-id="element-407">
                        Created visibility through Amazon brand store placements
                        and strategic digital merchandising
                      </li>
                    </ul>
                  </div>
                </li>
                <li data-id="element-408">
                  <div data-id="element-409">
                    <span className="font-medium text-gray-900" data-id="element-410">
                      R&D Partnership for Claims Innovation
                    </span>
                    <ul className="list-disc pl-6 mt-2 text-gray-700 space-y-1" data-id="element-411">
                      <li data-id="element-412">
                        Collaborated with R&D to identify and validate 27 new
                        claims without modifying formulations or packaging
                      </li>
                      <li data-id="element-413">
                        Created fresh messaging that highlighted previously
                        uncommunicated product benefits
                      </li>
                    </ul>
                  </div>
                </li>
                <li data-id="element-414">
                  <div data-id="element-415">
                    <span className="font-medium text-gray-900" data-id="element-416">
                      Test & Learn Methodology
                    </span>
                    <ul className="list-disc pl-6 mt-2 text-gray-700 space-y-1" data-id="element-417">
                      <li data-id="element-418">
                        Implemented A/B testing on the company website to
                        validate approaches before broader rollout
                      </li>
                      <li data-id="element-419">
                        Developed a continuous optimization framework that
                        ensured efficient resource allocation
                      </li>
                    </ul>
                  </div>
                </li>
              </ol>
            </div>
            <div className="mb-12" data-id="element-420">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-421">
                Execution & Results
              </h2>
              <div className="bg-[#F0FAFC] p-6 rounded-lg mb-8" data-id="element-422">
                <h3 className="text-xl font-semibold text-gray-800 mb-3" data-id="element-423">
                  Key Initiatives Implemented:
                </h3>
                <ul className="space-y-3" data-id="element-424">
                  <li className="flex" data-id="element-425">
                    <div className="w-2 h-2 rounded-full bg-[#0096B4] mt-2 mr-3" data-id="element-426"></div>
                    <div data-id="element-427">
                      <span className="font-medium text-gray-900" data-id="element-428">
                        Self-Funded, Always-On Strategy
                      </span>
                      <p className="text-gray-700" data-id="element-429">
                        Created sustainable marketing approach that required no
                        additional budget allocation
                      </p>
                    </div>
                  </li>
                  <li className="flex" data-id="element-430">
                    <div className="w-2 h-2 rounded-full bg-[#0096B4] mt-2 mr-3" data-id="element-431"></div>
                    <div data-id="element-432">
                      <span className="font-medium text-gray-900" data-id="element-433">
                        Inbound Marketing Campaigns
                      </span>
                      <p className="text-gray-700" data-id="element-434">
                        Developed targeted content highlighting new claims and
                        benefits to attract interested consumers
                      </p>
                    </div>
                  </li>
                  <li className="flex" data-id="element-435">
                    <div className="w-2 h-2 rounded-full bg-[#0096B4] mt-2 mr-3" data-id="element-436"></div>
                    <div data-id="element-437">
                      <span className="font-medium text-gray-900" data-id="element-438">
                        Digital Content Refresh
                      </span>
                      <p className="text-gray-700" data-id="element-439">
                        Updated online product pages with new claims and
                        messaging to improve search visibility and conversion
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3" data-id="element-440">
                Impact:
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-441">
                <div className="bg-[#E0F5F8] p-4 rounded-lg text-center" data-id="element-442">
                  <div className="text-3xl font-bold text-[#0096B4] mb-1" data-id="element-443">
                    +21%
                  </div>
                  <div className="text-sm text-gray-700" data-id="element-444">
                    Performance Turnaround
                  </div>
                </div>
                <div className="bg-[#E0F5F8] p-4 rounded-lg text-center" data-id="element-445">
                  <div className="text-3xl font-bold text-[#0096B4] mb-1" data-id="element-446">
                    +$6.5M
                  </div>
                  <div className="text-sm text-gray-700" data-id="element-447">
                    Sales Forecast Increase
                  </div>
                </div>
                <div className="bg-[#E0F5F8] p-4 rounded-lg text-center" data-id="element-448">
                  <div className="text-3xl font-bold text-[#0096B4] mb-1" data-id="element-449">
                    -12% to +9%
                  </div>
                  <div className="text-sm text-gray-700" data-id="element-450">
                    YOY Performance Change
                  </div>
                </div>
              </div>
              <p className="text-gray-700" data-id="element-451">
                Within just 6 months, we successfully transformed the
                performance of these product lines from a -12% year-over-year
                decline to +9% growth, representing a remarkable 21% swing in
                performance. This turnaround generated an additional $6.5
                million in sales beyond forecast, all achieved with no
                additional budget allocation.
              </p>
            </div>
            <div data-id="element-452">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-453">
                Key Learnings
              </h2>
              <ol className="list-decimal pl-6 space-y-4" data-id="element-454">
                <li data-id="element-455">
                  <div data-id="element-456">
                    <span className="font-medium text-gray-900" data-id="element-457">
                      Look for hidden value in existing assets
                    </span>
                    <p className="text-gray-700" data-id="element-458">
                      Products that aren't in the spotlight can still deliver
                      significant growth with the right strategic approach and
                      messaging refresh.
                    </p>
                  </div>
                </li>
                <li data-id="element-459">
                  <div data-id="element-460">
                    <span className="font-medium text-gray-900" data-id="element-461">
                      Cross-functional partnerships create opportunities
                    </span>
                    <p className="text-gray-700" data-id="element-462">
                      Working closely with R&D allowed us to find new claims and
                      benefits without product reformulation, demonstrating how
                      silos can be broken down for business impact.
                    </p>
                  </div>
                </li>
                <li data-id="element-463">
                  <div data-id="element-464">
                    <span className="font-medium text-gray-900" data-id="element-465">
                      Test & learn approach maximizes efficiency
                    </span>
                    <p className="text-gray-700" data-id="element-466">
                      By validating strategies before full rollout, we minimized
                      risk and optimized resource allocation, creating a
                      sustainable model for ongoing optimization.
                    </p>
                  </div>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>
      <section className="py-12 bg-[#F0FAFC]" data-id="element-467">
        <div className="container mx-auto px-4" data-id="element-468">
          <div className="max-w-3xl mx-auto" data-id="element-469">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-470">
              More Case Studies
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-471">
              <Link to="/case-study-2" className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200" data-id="element-472">
                <h3 className="text-xl font-semibold text-gray-800 mb-2" data-id="element-473">
                  Go-to-Market Strategy
                </h3>
                <p className="text-gray-600 mb-3" data-id="element-474">
                  Spearheaded new GTM strategy, driving 93% trial-to-purchase
                  conversion.
                </p>
                <span className="text-[#0096B4] font-medium" data-id="element-475">
                  Read case study →
                </span>
              </Link>
              <Link to="/case-study-3" className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200" data-id="element-476">
                <h3 className="text-xl font-semibold text-gray-800 mb-2" data-id="element-477">
                  CRM Program Development
                </h3>
                <p className="text-gray-600 mb-3" data-id="element-478">
                  Built Neutrogena's CRM program, fueling +236% traffic growth.
                </p>
                <span className="text-[#0096B4] font-medium" data-id="element-479">
                  Read case study →
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>;
}